import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Message } from '../types/chat/Message'
import { useSanitizeHtml } from '../composables/SanitizeHtml'

const REDIRECT_STORAGE_KEY = 'chat-store-redirect'

export const useChatStore = defineStore('chatStore', () => {
  const isFirstQuestionSent = ref<boolean>(false)
  const questionNumber = ref<number>(0)
  const messages = ref<Message[]>([])
  const { sanitizeHtml } = useSanitizeHtml()

  function increaseQuestionNumber(): void {
    questionNumber.value += 1
  }

  function setIsFirstQuestionSent(value: boolean): void {
    isFirstQuestionSent.value = value
  }

  function updateMessageWithError(index: number, error: string): void {
    messages.value[index] = {
      ...messages.value[index],
      isLoading: false,
      text: error,
      isError: true,
    }
  }

  function pushMessage(message: Message): void {
    messages.value.push(message)
  }

  function keepLastTwoMessages(): void {
    if (messages.value.length <= 2) return
    messages.value.splice(0, messages.value.length - 2)
  }

  function updateMessageWithAnswer(index: number, answer: string): void {
    messages.value[index] = {
      ...messages.value[index],
      isLoading: false,
      text: answer,
    }
  }

  function saveForRedirect(): void {
    try {
      const payload = {
        isFirstQuestionSent: isFirstQuestionSent.value,
        questionNumber: questionNumber.value,
        messages: messages.value,
      }
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
      console.warn('Failed to save chat state for redirect', e)
    }
  }

  function restoreAfterRedirect(): void {
    try {
      const raw = sessionStorage.getItem(REDIRECT_STORAGE_KEY)
      if (!raw) return

      const parsed = JSON.parse(raw)

      isFirstQuestionSent.value = !!parsed.isFirstQuestionSent
      questionNumber.value = parsed.questionNumber ?? 0

      const restored: Message[] = Array.isArray(parsed.messages) ? parsed.messages : []

      messages.value = restored.map(m => ({
        ...m,
        text: sanitizeHtml(m?.text),
      }))
    } catch (e) {
      console.warn('Failed to restore chat state after redirect', e)
    } finally {
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY)
    }
  }

  return {
    isFirstQuestionSent,
    setIsFirstQuestionSent,
    questionNumber,
    increaseQuestionNumber,
    messages,
    pushMessage,
    keepLastTwoMessages,
    updateMessageWithAnswer,
    updateMessageWithError,
    saveForRedirect,
    restoreAfterRedirect,
  }
})
